import { Injectable } from '@angular/core';

/**
 * Responsible for  transforming date to/from wire format - ISO 8601
 * Currently backend sends dates with EST offset - we can just ignore offset part since UI displays date only in this timezone.
 *
 */
@Injectable({
  providedIn: 'root'
})
export class DateCodecService {
  private static readonly DATE_WITH_TIME_ZONE_REGULAR_EXPRESSION: RegExp =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}?[-+]((\d{4})?|(\d{2}:\d{2})?)$/;

  /**
   * @param dateString String ISO 8601 date string  the yyyy-mm-ddThh:mm:ss[timezone offset]
   * @returns  Date
   * @description
   *
   * Parses date string and return it in the milliseconds
   */
  decode(dateString: string): Date | undefined {
    let dateAndTime: Array<string>;
    let partsDate: Array<string>;
    let partsTime: Array<string>;

    if (typeof dateString === 'string' && (dateString.length >= 19 && dateString.length <= 25)) {
      dateAndTime = dateString.split('T');
      partsDate = dateAndTime[0].split('-');
      if (dateAndTime[1] !== undefined) {
        partsTime = dateAndTime[1].substring(0, 8).split(':');
        return new Date(
          parseInt(partsDate[0], 10),
          parseInt(partsDate[1], 10) - 1,
          parseInt(partsDate[2], 10),
          parseInt(partsTime[0], 10),
          parseInt(partsTime[1], 10),
          parseInt(partsTime[2], 10));
      }
    }
    return undefined;
  }

  /**
   * @param date Date object to format
   * @returns yyyy-mm-ddThh:mm:ss or undefined if parameter is not a Date
   *
   * @description
   * Formats data object as ISO string
   */
  encode(date: Date): string | undefined {
    if (Object.prototype.toString.call(date) === '[object Date]') {
      return this.padNumber(date.getFullYear(), 4) + '-' +
        this.padNumber(date.getMonth() + 1, 2) + '-' +
        this.padNumber(date.getDate(), 2) +
        'T' +
        this.padNumber(date.getHours(), 2) + ':' +
        this.padNumber(date.getMinutes(), 2) + ':' +
        this.padNumber(date.getSeconds(), 2);
    }
    return undefined;
  }

  /**
   * @param  value String value to test against ISO pattern
   * @returns true if string matches ISO date standard
   */
  isDateString(value: string): boolean {
    if (typeof value === 'string' && (value.length >= 19 && value.charAt(10) === 'T' && value.length <= 25)) {
      return DateCodecService.DATE_WITH_TIME_ZONE_REGULAR_EXPRESSION.test(value);
    }
    return false;
  }

  private padNumber(numberToPad: number, pad: number): string {
    const padNum: number = Math.pow(10, pad);
    return numberToPad < padNum ? (String(padNum + numberToPad)).slice(1) : String(numberToPad);
  }

}
